import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Users, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { graphqlClient } from "@/lib/graphql";
import { retryNetworkFailures } from "@/lib/queryClient";
import { graphql } from "@/__generated__";
import LanguageFlag from "@/components/LanguageFlag";
import { formatUsdWhole } from "@/lib/money";

// Real published-course teaser for the logged-out homepage (top 3, newest
// first). Fetched client-side — the section sits below the fold, so a brief
// skeleton is fine; pre-launch (no published courses) it shows an honest
// "coming soon" rather than fabricated demo courses.
const HOMEPAGE_COURSES_QUERY = graphql(`
  query HomepageCourses {
    publishedCourses(limit: 3) {
      id
      title
      level
      priceCents
      thumbnailUrl
      enrollmentCount
      language { id name code }
      tutorProfile { id user { fullName } }
    }
  }
`);

const MarketplacePreview = () => {
  const { t, i18n } = useTranslation();
  const { data, isLoading } = useQuery({
    queryKey: ["homepageCourses"],
    queryFn: async () => {
      const res = await graphqlClient.request(HOMEPAGE_COURSES_QUERY);
      return res.publishedCourses;
    },
    staleTime: 5 * 60 * 1000,
    retry: retryNetworkFailures,
  });

  const courses = data ?? [];

  return (
    <div>
      {isLoading ? (
        <div className="grid gap-6 md:grid-cols-3 mb-10">
          {[0, 1, 2].map((i) => (
            <Card key={i} className="overflow-hidden">
              <div className="aspect-16/10 bg-muted animate-pulse" />
              <CardContent className="p-4 space-y-3">
                <div className="h-4 w-2/3 rounded bg-muted animate-pulse" />
                <div className="h-3 w-1/2 rounded bg-muted animate-pulse" />
                <div className="h-4 w-1/3 rounded bg-muted animate-pulse" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : courses.length > 0 ? (
        <div className="grid gap-6 md:grid-cols-3 mb-10">
          {courses.map((course) => (
            <Card key={course.id} className="text-left hover:shadow-soft transition-shadow overflow-hidden">
              <div className="aspect-16/10 bg-muted border-b border-border flex items-center justify-center overflow-hidden">
                {course.thumbnailUrl ? (
                  <img src={course.thumbnailUrl} alt={course.title} loading="lazy" className="w-full h-full object-cover" />
                ) : (
                  <LanguageFlag code={course.language.code} className="w-16 h-16" />
                )}
              </div>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <LanguageFlag code={course.language.code} className="w-5 h-5" />
                  {course.level && <Badge variant="outline" className="text-xs">{course.level}</Badge>}
                </div>
                <h4 className="font-semibold text-foreground mb-1">{course.title}</h4>
                {/* A tutor whose public page is off has no name for a visitor (#1518): no author line. */}
                {course.tutorProfile.user.fullName ? (
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-5 h-5 rounded-full bg-muted-foreground/20" />
                    <p className="text-sm text-muted-foreground">{course.tutorProfile.user.fullName}</p>
                  </div>
                ) : (
                  <div className="mb-3" />
                )}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1 text-sm text-muted-foreground">
                    <Users className="w-3.5 h-3.5" />
                    {course.enrollmentCount.toLocaleString(i18n.language)} {t("marketplacePreview.studentsLabel")}
                  </div>
                  <span className="font-bold text-foreground">{formatUsdWhole(course.priceCents, i18n.language)}</span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="text-center py-10 mb-10 text-muted-foreground">
          {t("marketplacePreview.empty")}
        </div>
      )}

      <div className="text-center">
        <Button variant="default" size="lg" asChild>
          <Link to="/signup">
            {/* With an empty catalog, "Browse Courses" would promise something
                the /signup link can't deliver — label the button for what it
                actually does until courses exist. */}
            {courses.length > 0 ? t("marketplacePreview.browseCourses") : t("nav.getStarted")}
            <ArrowRight className="w-4 h-4" />
          </Link>
        </Button>
      </div>
    </div>
  );
};

export default MarketplacePreview;
